import type { ContentfulImageSchema } from '@ecomm/contentful-components'
import { getContentfulImageFromGatsbyData } from '@ecomm/contentful-image-utils/getContentfulImageFromGatsbyData'
import type { GatsbyImageSchema } from '@ecomm/contentful-schemas'
import classNames from 'classnames'
import { not } from 'fp-ts/lib/Predicate'
import { map } from 'fp-ts/lib/ReadonlyArray'
import { identity } from 'fp-ts/lib/function'
import { useMemo, useState } from 'react'
import { P, match } from 'ts-pattern'

import { ImageGalleryCurrent } from './ImageGalleryCurrent'
import { ImageGalleryPager } from './ImageGalleryPager'

type ProductImages = ReadonlyArray<ContentfulImageSchema | GatsbyImageSchema>
type ImageGalleryProps = {
  readonly productImages: ProductImages
  readonly thumbnails?: ProductImages
  readonly isModal?: boolean
  readonly features?: Record<string, string>
}

const useContentfulImages = (image: ProductImages) =>
  useMemo(() => getContentfulImages(image), [image])

const containsGatsbyImages = (
  images: ProductImages
): images is ReadonlyArray<GatsbyImageSchema> =>
  images.some(image => 'gatsbyImageData' in image)

const containsContentfuImages = (
  images: ProductImages
): images is ReadonlyArray<ContentfulImageSchema> =>
  not(containsGatsbyImages)(images)

const getContentfulImages = (images: ProductImages) =>
  match(images)
    .with(P.when(containsGatsbyImages), map(getContentfulImageFromGatsbyData))
    .with(P.when(containsContentfuImages), identity)
    .run()

export function ImageGallery({
  features = {},
  productImages,
  thumbnails,
  isModal = false
}: ImageGalleryProps) {
  const [currentImage, setCurrentImage] = useState(0)

  const pagerImages = thumbnails || productImages
  const contentfulPagerImages = useContentfulImages(pagerImages)
  const current = useContentfulImages(productImages)[currentImage]

  return (
    <div
      className={classNames(
        'col-span-12 mb-6 inline-grid max-w-none grid-cols-12 items-stretch',
        {
          'md:col-span-12 lg:col-span-6 lg:row-span-2': !isModal
        }
      )}
      data-component="ImageGallery"
    >
      <ImageGalleryPager
        currentImage={currentImage}
        isModal={isModal}
        onPagerClick={setCurrentImage}
        productImages={contentfulPagerImages}
      />
      {current ? (
        <ImageGalleryCurrent
          current={current}
          feature={features[currentImage]}
          isModal={isModal}
        />
      ) : null}
    </div>
  )
}
